import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import {
  Avatar, Box, Button, Card, Chip, CircularProgress, Divider, IconButton,
  Skeleton, Stack, Tab, TextField, Tooltip, Typography
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Swal from 'sweetalert2';
import Icon from '../../../components/icon';
import apiUrl from '../../../configs/constant';
import api from '../../../utils/axiosConfig';
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto';
import { useToast } from '../../../components/ToastProvider';
import './work-order.scss';

const tabItems = [
  { value: '0', label: 'Daftar Work Order', shortLabel: 'Work Order', icon: 'mdi:clipboard-text-outline' },
  { value: '2', label: 'Outstanding Sales Order', shortLabel: 'Sales Order', icon: 'mdi:cart-arrow-right' },
  { value: '3', label: 'Outstanding Permintaan 17', shortLabel: 'Permintaan 17', icon: 'mdi:file-clock-outline' },
];

const errorMessage = (error: any) => error?.response?.data?.message || error?.message || 'Data Work Order gagal dimuat.';

export default function ProductionWorkOrderIndex() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const requestController = useRef<AbortController | null>(null);
  const [tabs, setTabs] = useState('0');
  const [tglAwal, setTglAwal] = useState(moment().subtract(3, 'months').startOf('month').format('YYYY-MM-DD'));
  const [tglAkhir, setTglAkhir] = useState(moment().endOf('month').format('YYYY-MM-DD'));
  const [listData, setListData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const getData = async (mode = tabs) => {
    if (!tglAwal || !tglAkhir) return setError('Tanggal awal dan tanggal akhir wajib diisi.');
    if (tglAwal > tglAkhir) return setError('Tanggal awal tidak boleh melebihi tanggal akhir.');
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setLoading(true);
    setError('');
    setListData([]);
    try {
      const { data } = await api.get('/production/work-order/list', { params: { awal: tglAwal, akhir: tglAkhir, mode }, signal: controller.signal });
      if (data?.success === false) throw new Error(data.message || 'Data Work Order gagal dimuat.');
      if (!controller.signal.aborted) setListData(Array.isArray(data?.data) ? data.data : []);
    } catch (requestError: any) {
      if (requestError?.code !== 'ERR_CANCELED' && !controller.signal.aborted) setError(errorMessage(requestError));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    getData('0');
    return () => requestController.current?.abort();
  }, []);

  const handleChangeTabs = (_event: SyntheticEvent, newValue: string) => {
    setTabs(newValue);
    getData(newValue);
  };

  const handleDelete = async (row: any) => {
    const confirmation = await Swal.fire({
      title: 'Hapus Work Order?',
      text: `${row.kode || row.id} akan dihapus dari daftar Work Order.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonText: 'Batal',
      confirmButtonText: 'Hapus',
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;
    setDeletingId(row.id);
    try {
      const { data } = await api.delete(`/production/work-order/delete/${row.id}`);
      if (data?.success === false) throw new Error(data.message || 'Work Order gagal dihapus.');
      showToast(data?.message || 'Work Order berhasil dihapus.', 'success');
      await getData(tabs);
    } catch (deleteError) {
      showToast(errorMessage(deleteError), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const ActionButtons = ({ row }: { row: any }) => {
    const actions = [
      { title: 'Lihat detail', icon: 'mdi:arrow-right', className: 'detail-action', onClick: () => navigate(`/production/work-order/detail/${row.id}`) },
      { title: 'Cetak Work Order', icon: 'mdi:printer-outline', className: '', onClick: () => window.open(`${apiUrl.baseUrl}/production/work-order/pdf/${row.id}`, '_blank') },
      { title: 'Cetak List BOM', icon: 'mdi:file-tree-outline', className: '', onClick: () => window.open(`${apiUrl.baseUrl}/production/work-order/pdf-bom/${row.id}`, '_blank') },
      { title: 'Hapus Work Order', icon: 'mdi:delete-outline', className: 'delete-action', onClick: () => handleDelete(row) },
    ];
    return <Box className="wo-row-actions">{actions.map(action => <Tooltip key={action.title} title={action.title} placement="top"><span><IconButton className={action.className} size="small" onClick={action.onClick} disabled={deletingId === row.id}>{deletingId === row.id && action.className === 'delete-action' ? <CircularProgress size={17} /> : <Icon icon={action.icon} />}</IconButton></span></Tooltip>)}</Box>;
  };

  const actionData = [{
    key: 'aksi', sortable: false, minWidth: 170, field: 'aksi', align: 'center', headerAlign: 'center', headerName: 'Aksi',
    renderCell: ({ row }: { row: any }) => <ActionButtons row={row} />,
  }];

  const outstandingAction = (source: 'so' | 'request') => [{
    key: 'aksi', sortable: false, minWidth: 170, field: 'aksi', align: 'center', headerAlign: 'center', headerName: 'Aksi',
    renderCell: ({ row }: { row: any }) => <Button className="create-row-button" size="small" variant="contained" startIcon={<Icon icon="mdi:plus" />} onClick={() => navigate(`/production/work-order/add?source=${source}&id=${row.id}`)}>Buat Work Order</Button>,
  }];

  const convertComponent = [{
    key: 'gambar',
    renderCell: (cellData: any) => {
      const imageUrl = cellData?.data?.gambar ?? cellData?.row?.data?.gambar ?? cellData?.value;
      if (!imageUrl) return <Avatar variant="rounded" className="wo-product-placeholder"><Icon icon="mdi:package-variant-closed" /></Avatar>;
      return <button className="wo-product-image-button" type="button" onClick={() => Swal.fire({ title: 'Preview Gambar', imageUrl, imageWidth: 460, imageAlt: 'Preview produk', showCloseButton: true, showConfirmButton: false })}><img src={imageUrl} alt="Produk" /></button>;
    },
  }];

  const currentTab = tabItems.find(item => item.value === tabs) || tabItems[0];
  const renderGrid = () => {
    if (loading) return <TableLoading />;
    if (error) return <Box className="wo-state"><Icon icon="mdi:cloud-alert-outline" /><Typography variant="h6" fontWeight={800}>Data gagal dimuat</Typography><Typography color="text.secondary">{error}</Typography><Button variant="outlined" onClick={() => getData(tabs)} startIcon={<Icon icon="mdi:refresh" />}>Coba lagi</Button></Box>;
    if (!listData.length) return <Box className="wo-state"><Icon icon="mdi:clipboard-text-search-outline" /><Typography variant="h6" fontWeight={800}>Belum ada data</Typography><Typography color="text.secondary">Tidak ada {currentTab.shortLabel.toLowerCase()} pada filter yang dipilih.</Typography></Box>;
    if (tabs === '2') return <MsiDatagrid array={listData} actions={outstandingAction('so')} groupPanel={false} />;
    if (tabs === '3') return <MsiDatagrid array={listData} actions={outstandingAction('request')} hiddenColumns={['mmbarang_id', 'created_at', 'created_by', 'is_aktif']} customRender={convertComponent} groupPanel={false} />;
    return <MsiDatagrid array={listData} actions={actionData} hiddenColumns={['dokumen', 'tgl_aktif', 'tgl_selesai', 'is_aktif']} customRender={convertComponent} groupPanel={false} />;
  };

  return <div className="production-wo-page">
    <Box className="production-wo-heading">
      <Box><Typography variant="h4" fontWeight={850}>Work Order</Typography><Typography color="text.secondary">Kelola Work Order produksi dan tindak lanjuti permintaan yang masih outstanding.</Typography></Box>
      <Stack direction="row" spacing={1}><Tooltip title="Muat ulang"><span><IconButton className="refresh-button" onClick={() => getData(tabs)} disabled={loading}><Icon icon="mdi:refresh" /></IconButton></span></Tooltip><Button size="large" variant="contained" onClick={() => navigate('/production/work-order/add')} startIcon={<Icon icon="mdi:plus" />}>Buat Work Order</Button></Stack>
    </Box>

    <Card className="production-wo-shell">
      <Box className="wo-filter-bar">
        <TextField size="small" label="Tanggal awal" type="date" value={tglAwal} onChange={event => setTglAwal(event.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="Tanggal akhir" type="date" value={tglAkhir} onChange={event => setTglAkhir(event.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" onClick={() => getData(tabs)} disabled={loading} startIcon={loading ? <CircularProgress size={17} color="inherit" /> : <Icon icon="mdi:filter-outline" />}>{loading ? 'Memuat...' : 'Tampilkan'}</Button>
        <Box className="filter-result"><Icon icon="mdi:calendar-range" /><Typography variant="body2" color="text.secondary">{moment(tglAwal).format('DD MMM YYYY')} — {moment(tglAkhir).format('DD MMM YYYY')}</Typography>{!loading && !error && <Chip size="small" label={`${listData.length} data`} />}</Box>
      </Box>

      <TabContext value={tabs}>
        <Box className="wo-tabs"><TabList scrollButtons="auto" variant="scrollable" onChange={handleChangeTabs} aria-label="Kategori Work Order">{tabItems.map(item => <Tab key={item.value} value={item.value} icon={<Icon icon={item.icon} />} iconPosition="start" label={item.label} />)}</TabList></Box>
        <Divider />
        <TabPanel value={tabs} className="wo-grid-panel">{renderGrid()}</TabPanel>
      </TabContext>
    </Card>
  </div>;
}

function TableLoading() {
  return <Box className="wo-table-loading"><Box className="loading-toolbar"><Skeleton width={220} height={38} /><Skeleton width={120} height={38} /></Box>{Array.from({ length: 7 }).map((_, index) => <Box className="loading-row" key={index}><Skeleton variant="rounded" width={48} height={48} /><Box><Skeleton width={`${55 + (index % 3) * 12}%`} /><Skeleton width="42%" /></Box><Skeleton width="70%" /><Skeleton width="65%" /><Skeleton width={110} /></Box>)}</Box>;
}
